<template>
  <div
    v-if="processedRecommendations.length > 0"
    ref="mainContainer"
    class="cart-upsells bg-natural-white-60 overflow-hidden p-2.5"
    :class="isExpanded ? 'pb-8' : 'pb-2'"
  >
    <button
      class="flex w-full cursor-pointer items-center justify-between"
      @click="handleToggleUpsells()"
    >
      <h3 class="font-regular text-xs tracking-wider uppercase" v-text="title" />
      <span
        class="flex items-center justify-center rounded-full border border-black text-xs font-medium uppercase"
      >
        <Icon
          :class="isExpanded ? 'rotate-180' : ''"
          name="chevron-up"
          class-name="size-5 transform-origin-center transition-transform ease-in-out duration-300"
        />
      </span>
    </button>
    <div ref="upsellsContainer" class="relative translate-y-2.5 overflow-visible">
      <div ref="swiperContainer" class="swiper !overflow-visible md:mb-2.5">
        <div class="swiper-wrapper">
          <div
            v-for="item in processedRecommendations"
            :key="item.cfId"
            class="swiper-slide !flex h-auto items-center justify-between rounded-[10px] border border-gray-200 bg-white px-2.5 py-4"
          >
            <a :href="getProductUrl(item.handle)" class="mb-2 block w-fit">
              <img
                :src="item.image"
                :alt="item.productName"
                class="aspect-square size-20 rounded object-contain"
              />
            </a>
            <div class="w-[70%]">
              <div class="mb-2.5 flex items-center justify-between">
                <a
                  class="font-regular text-light-charcoal text-sm text-nowrap"
                  :href="getProductUrl(item.handle)"
                  v-text="item.productName"
                />
                <div
                  v-if="formatPrice(item.sizes, 'compareAtPrice')"
                  class="flex items-center gap-x-1"
                >
                  <p
                    class="text-red text-sm font-medium"
                    v-text="formatPrice(item.sizes, 'price')"
                  />
                  <p
                    class="text-light-charcoal text-sm font-medium line-through"
                    v-text="formatPrice(item.sizes, 'compareAtPrice')"
                  />
                </div>
                <p v-else class="text-sm font-medium" v-text="formatPrice(item.sizes, 'price')" />
              </div>
              <div>
                <div class="mb-2.5 flex gap-x-3">
                  <button
                    v-for="(color, index) in item.swatchColors"
                    :key="index"
                    class="h-5 w-5 cursor-pointer rounded-full border"
                    :class="[
                      item.selectedColor === color ? 'border border-black' : 'border-transparent',
                    ]"
                    :aria-label="`Color ${color}`"
                    @click="handleSwatchClick(item, color)"
                  >
                    <div
                      :style="{ backgroundColor: color }"
                      class="m-auto size-[14px] rounded-full"
                    />
                  </button>
                </div>
                <div class="flex">
                  <div class="relative w-full min-w-[165px]">
                    <label
                      :for="`size-select-${item.cfId}`"
                      class="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 rounded border border-black"
                    >
                      <span class="sr-only">Select size</span>
                      <Icon name="chevron-down" class-name="size-4 pointer-events-none" />
                    </label>
                    <select
                      v-if="item.sizes && item.sizes.length > 0"
                      :id="`size-select-${item.cfId}`"
                      v-model="item.selected_variant_id"
                      class="border-back w-full min-w-[100px] appearance-none rounded border p-2 text-xs"
                      aria-label="Select size"
                      @change="handleSizeChange($event, item)"
                    >
                      <option value="Select Size" disabled />
                      <option
                        v-for="size in item.sizes"
                        :key="size.legacyResourceId"
                        class="text-xs"
                        :value="size.legacyResourceId"
                        :disabled="!size.isAvailable"
                        v-text="`${props.sizeLabel} ${size.title}`"
                      />
                    </select>
                  </div>
                  <button
                    class="w-full cursor-pointer text-sm font-medium underline md:no-underline md:hover:underline"
                    @click="handleAddItem(item)"
                    v-text="props.upsellsAddLabel"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="cart-upsells-navigation justify-right hidden w-fit gap-x-2 md:mt-2 md:ml-auto md:flex"
          aria-label="Upsell navigation"
        >
          <button
            ref="prevButton"
            class="cart-upsells-prev-button cursor-pointer rounded-full border border-black"
            aria-label="Previous upsell item"
          >
            <Icon name="chevron-left" class-name="size-5 text-neutral-900" />
          </button>
          <button
            ref="nextButton"
            class="cart-upsells-next-button cursor-pointer rounded-full border border-black"
            aria-label="Next upsell item"
          >
            <Icon name="chevron-right" class-name="size-5 text-neutral-900" />
          </button>
        </div>
        <div
          ref="paginationContainer"
          class="swiper-pagination cart-upsells-pagination flex justify-center md:hidden"
          aria-label="Slider pagination"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import gsap from 'gsap';
import Swiper from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import 'swiper/css';
import 'swiper/css/navigation';

import Icon from '~/components/icon-svg.vue';
import { useCart } from '~/composables/use-cart';
import { ANIMATION_EASES } from '~/constants/animations';
import { TRACK_CART_SOURCE } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import { formatCurrency } from '~/utils/currency';
import getItemCartProperties from '~/utils/item-cart-properties';

const props = defineProps({
  addButtonText: {
    default: 'Add+',
    type: String,
  },
  recommendations: {
    default: () => [],
    type: Array,
  },
  sizeLabel: {
    default: 'Size:',
    type: String,
  },
  title: {
    default: 'Recommended for you',
    type: String,
  },
  upsellsAddLabel: {
    default: 'Add+',
    type: String,
  },
});

const SWIPER_OPTIONS = {
  breakpoints: {
    768: {
      navigation: {
        enabled: true,
      },
      pagination: {
        enabled: false,
      },
      slidesPerView: 1.5,
    }, // md
  },
  centeredSlides: false,
  modules: [Pagination, Navigation, A11y],
  navigation: {
    enabled: false,
    nextEl: '.cart-upsells-next-button',
    prevEl: '.cart-upsells-prev-button',
  },
  pagination: {
    clickable: true,
    el: '.swiper-pagination',
    enabled: true,
  },
  slidesPerView: 1,
  spaceBetween: 8,
};

const { addSingleVariant, cart } = useCart();

const swiperContainer = ref(null);
const upsellsContainer = ref(null);
const paginationContainer = ref(null);
const prevButton = ref(null);
const nextButton = ref(null);
const mainContainer = ref(null);
const isExpanded = ref(true);
const selectedColors = ref({});

let swiperInstance = null;
let collapseTimeline = null;

const initializeSwiper = () => {
  if (
    swiperContainer.value &&
    prevButton.value &&
    nextButton.value &&
    paginationContainer.value &&
    processedRecommendations.value.length > 0
  ) {
    swiperInstance = new Swiper(swiperContainer.value, {
      ...SWIPER_OPTIONS,
    });
  }
};

const destroySwiper = () => {
  if (swiperInstance) {
    swiperInstance.destroy(true, true);
    swiperInstance = null;
  }
};

const processedRecommendations = computed(() => {
  if (!props.recommendations) return [];
  const propsRecommendations = props.recommendations || [];
  const cartItemNames = cart.value?.items?.reduce((acc, item) => {
    const productMasterName = item.properties._master_name || 'unknown';
    return {
      ...acc,
      [productMasterName]: acc[productMasterName]
        ? acc[productMasterName] + item.quantity
        : item.quantity,
    };
  }, {});
  const filteredRecommendations = propsRecommendations.filter(
    (recommendation) => !cartItemNames[recommendation[0].productName],
  );

  return filteredRecommendations
    .map((items) => {
      if (!Array.isArray(items) || items.length === 0) {
        return null;
      }
      const productId = items[0].productId;

      // Initialize selected color for this product if not already set
      if (!selectedColors.value[productId]) {
        selectedColors.value[productId] = items[0].swatchColors[0];
      }

      const selectedColor = selectedColors.value[productId];
      const selectedVariant =
        items.find((variant) => variant.swatchColors.includes(selectedColor)) || items[0];

      return {
        ...items[0], // Use first item as base
        allColorVariants: items,
        // Override with selected variant data
        cfId: selectedVariant.cfId,
        collectionProductCardImage: selectedVariant.collectionProductCardImage,
        colorName: selectedVariant.colorName,
        handle: selectedVariant.handle,
        image: selectedVariant.image,
        name: selectedVariant.name,
        selected_variant_id:
          selectedVariant.sizes?.find((s) => s.isAvailable)?.legacyResourceId || '',
        selectedColor: selectedColor,
        sizes: selectedVariant.sizes,
        swatchColors: items.flatMap((item) => item.swatchColors),
      };
    })
    .filter(Boolean);
});

onMounted(async () => {
  if (processedRecommendations.value.length > 0) {
    initializeSwiper();
  }

  await nextTick();
  setupCollapseTimeline();
});

onUnmounted(() => {
  destroySwiper();
});

const getProductUrl = (handle) => {
  if (!handle) return '#';
  return `/products/${handle}`;
};

const formatPrice = (sizes, field = 'price') => {
  if (sizes && sizes.length > 0) {
    const availableSize = sizes.find((s) => s.isAvailable);
    if (availableSize && !!availableSize[field]) {
      const priceInCents = parseFloat(availableSize[field]) * 100;
      return formatCurrency(priceInCents);
    }
    // Fallback to first size price if no specific available one is found (e.g. all out of stock but price still shown)
    // Ensure sizes[0] exists before accessing its price
    if (sizes[0] && !!sizes[0][field]) {
      const priceInCents = parseFloat(sizes[0][field]) * 100;
      return formatCurrency(priceInCents);
    }
  }
  return '';
};

const setupCollapseTimeline = () => {
  if (!upsellsContainer.value) return;

  if (collapseTimeline) {
    collapseTimeline.kill();
  }

  const container = upsellsContainer.value;
  const currentHeight = container.scrollHeight;
  const offset = 0;

  gsap.set(container, { maxHeight: isExpanded.value ? `${currentHeight}px` : '0px' });

  collapseTimeline = gsap.timeline({ paused: true });

  collapseTimeline.fromTo(
    container,
    { maxHeight: '0px' },
    {
      duration: 0.3,
      ease: ANIMATION_EASES.POWER3_OUT,
      maxHeight: `${currentHeight}px`,
    },
    0,
  );

  collapseTimeline.fromTo(
    mainContainer.value,
    {
      y: `${offset}px`,
    },
    {
      duration: 0.3,
      ease: ANIMATION_EASES.POWER3_OUT,
      y: 0,
    },
    0,
  );

  if (isExpanded.value) {
    collapseTimeline.progress(1, false);
  } else {
    collapseTimeline.progress(0, false);
  }
};

const handleSwatchClick = (item, color) => {
  selectedColors.value[item.productId] = color;
};

const handleAddItem = async (item) => {
  if (!item.selected_variant_id || item.selected_variant_id === '') {
    DataDogLogs.error(
      '[Allbirds component] - Cart Upsells - handleAddItem - No size selected',
      { item },
      new Error('No size selected'),
    );
    return;
  }

  const cleanId = Number(item.selected_variant_id);
  const properties = getItemCartProperties({
    colorName: item.colorName,
    compareAtPrice: item.compareAtPrice,
    masterName: item.productName,
  });

  try {
    await addSingleVariant(cleanId, 1, properties);

    // Analytics, pull the added product and send the analytics
    const productToAdd = cart?.value?.items.find((item) => item.id === cleanId);
    RudderstackClient.productAdded({
      ajaxCartItems: cart?.value?.items,
      productAddedAjaxCart: productToAdd,
      productAddedSource: TRACK_CART_SOURCE.UPSELL,
      productSizeSelected: productToAdd?.variant_title,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds component] - Cart Upsells - handleAddItem - Error adding item',
      {
        cleanId,
        item,
      },
      error,
    );
  }
};

const handleToggleUpsells = () => {
  isExpanded.value = !isExpanded.value;
};

watch(
  processedRecommendations,
  async (newSlides, _oldSlides) => {
    let currentSlideIndex = 0;
    if (swiperInstance && swiperInstance.activeIndex !== undefined) {
      currentSlideIndex = swiperInstance.activeIndex;
    }

    destroySwiper();

    if (newSlides.length > 0) {
      await nextTick();
      initializeSwiper();

      if (swiperInstance && currentSlideIndex > 0 && currentSlideIndex < newSlides.length) {
        swiperInstance.slideTo(currentSlideIndex, 0); // 0 = no animation
      }
    }
    // Re-setup collapse timeline regardless of newSlides length,
    // as scrollHeight might change (e.g. from items to no items or vice-versa)
    await nextTick();
    setupCollapseTimeline();
  },
  { deep: true },
);

watch(isExpanded, (newIsExpanded) => {
  if (!collapseTimeline) {
    setupCollapseTimeline();
    if (!collapseTimeline) return;
  }

  if (newIsExpanded) {
    collapseTimeline.play();
  } else {
    collapseTimeline.reverse();
  }
});
</script>
